import React, {useEffect, useMemo, useState} from 'react';
import {List} from 'immutable';

import {addToast, LoadingSpinner, Menu, Modal} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import {resource} from '@albert-io/json-api-framework/request/builder';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import manageRosterModalCollection from 'client/Classrooms/Classroom/Teacher/ManageRoster/manageRosterModalCollection';
import {
  getClassroomQuery,
  getCoteachersQuery,
  getInvitationsQuery,
  getStudentsQuery
} from 'client/Classrooms/Classroom/Teacher/People/people.queries';
import PeopleContext from 'client/Classrooms/Classroom/Teacher/People/people.context';
import {AddCoteacherModal} from 'client/Classrooms/Classroom/Teacher/People/Coteachers/AddCoteacher/AddCoteacher.react';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {normalizeError} from 'lib/normalizeError';

import {EDGE_MENU_POPPER_PROPS} from '../config/menuPopperProps';

// Reuse the legacy roster modals
type StudentModalKey = 'classCode' | 'email' | 'LMS' | 'googleClassroom';
type ModalKey = StudentModalKey | 'coteacher' | '';

// A mid-refetch interest can hold a stub record without generated getters (crashed the header on
// class switch, #19130) — every read below must be optional.
type InviteClassroom = ClassroomModelV1 & {
  getSchool?: () => {getId?: () => string} | null;
  getStudents?: () => List<unknown> | undefined;
};

const studentModals = manageRosterModalCollection as Record<
  StudentModalKey,
  React.ComponentType<{classroom: ClassroomModelV1; handleClose: () => void}>
>;

// Co-teacher parity gate: only teachers approved on the class's school may add co-teachers
const getApprovedPersonnelQuery = (schoolId: string, teacherId: string) =>
  resource('school_personnel_v1')
    .mandarkEndpoint(['school_personnel_v1'])
    .filter({school_v5: schoolId, teacher_v1: teacherId});

const TypedManageRosterContext = ManageRosterContext as unknown as React.Context<unknown>;
const TypedPeopleContext = PeopleContext as unknown as React.Context<unknown>;

interface Props {
  classroomId: string;
  classroom: ClassroomModelV1;
  /** The on-color Invite button; rendered as the menu trigger so the header owns its styling. */
  trigger: React.ComponentProps<typeof Menu.Trigger>['children'];
}

export const InviteMenu = ({classroomId, classroom: initialClassroom, trigger}: Props) => {
  const [modalToShow, setModalToShow] = useState<ModalKey>('');
  // The banner query is lean; the roster/license payload the modals need is fetched on first modal
  // open. Doubles as the local copy so "Generate a new code" reflects in place.
  const [fullClassroom, setFullClassroom] = useState<ClassroomModelV1 | null>(null);

  const queries = useMemo(
    () => ({
      classroom: getClassroomQuery(classroomId),
      invitations: getInvitationsQuery(classroomId),
      students: getStudentsQuery(classroomId)
    }),
    [classroomId]
  );

  useEffect(() => {
    if (!modalToShow || fullClassroom) {
      return undefined;
    }
    let isStale = false;
    const query = queries.classroom;
    // A previously-failed fetch is cached as an error stub — re-dirty so this attempt refetches.
    if (!query.isResourceValid()) {
      query.invalidateInterest();
    }
    query
      .getResourcePromise()
      .then((model) => {
        if (!isStale) {
          setFullClassroom(model as ClassroomModelV1);
        }
      })
      .catch((error) => {
        if (!isStale) {
          setModalToShow('');
          addToast({
            color: 'negative',
            title: "Couldn't load the class roster",
            message: normalizeError(error).message
          });
        }
      });
    return () => {
      isStale = true;
    };
  }, [modalToShow, fullClassroom, queries.classroom]);

  const classroom = fullClassroom ?? initialClassroom;

  const rosterContext = useMemo(
    () => ({
      classroom,
      queries,
      students: (classroom as InviteClassroom).getStudents?.() ?? List(),
      setIsReady: () => {},
      setClassroom: setFullClassroom
    }),
    [classroom, queries]
  );

  const schoolId = (classroom as InviteClassroom).getSchool?.()?.getId?.() ?? '';
  const teacherId = masqueradeStore.getUserIdByMasqueradeState() ?? '';

  const personnelQuery = useMemo(
    () => getApprovedPersonnelQuery(schoolId, teacherId),
    [schoolId, teacherId]
  );
  // Readiness keyed to the query itself: saving a school change swaps personnelQuery, and
  // useAreLatestQueriesReady would stay "ready" from the old one while the new resource is still
  // an unhydrated stub (crashed on record.getStatus, #18328) — and would never fetch it.
  const [readyPersonnelQuery, setReadyPersonnelQuery] = useState<ReturnType<
    typeof getApprovedPersonnelQuery
  > | null>(null);
  useEffect(() => {
    let isStale = false;
    personnelQuery
      .getResourcePromise()
      .then(() => {
        if (!isStale) {
          setReadyPersonnelQuery(personnelQuery);
        }
      })
      // No personnel record readable -> the co-teacher section just stays hidden.
      .catch(() => {});
    return () => {
      isStale = true;
    };
  }, [personnelQuery]);
  const isPersonnelReady = readyPersonnelQuery === personnelQuery;

  const approvedPersonnel = isPersonnelReady
    ? (personnelQuery.getResource() as
        | {some: (cb: (record: {getStatus?: () => string}) => boolean) => boolean}
        | undefined)
    : undefined;
  const canAddCoteacher = Boolean(
    // A mid-refetch interest can hold stub records without generated getters — skip them.
    approvedPersonnel?.some((record) => record.getStatus?.() === 'approved')
  );

  const close = () => setModalToShow('');
  // Modals render only once the full (roster-bearing) classroom is loaded.
  const StudentModal =
    fullClassroom && modalToShow && modalToShow !== 'coteacher' ? studentModals[modalToShow] : null;

  return (
    <>
      <TypedManageRosterContext.Provider value={rosterContext}>
        <Menu
          closeOnItemClick
          popperProps={EDGE_MENU_POPPER_PROPS}
          position='bottom-end'
          positionFixed
        >
          <Menu.Trigger>{trigger}</Menu.Trigger>
          <Menu.Content className='[&_.o-menu-content]:p-x2! text-left'>
            <Menu.Item
              className='px-x0-half py-x1'
              padding='none'
              readonly
              textProps={{color: 'secondary', size: 's'}}
            >
              Invite Students:
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: 'binary', iconStyle: 'regular'}}
              onClick={() => setModalToShow('classCode')}
            >
              Display Class Code
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: 'envelope', iconStyle: 'regular'}}
              onClick={() => setModalToShow('email')}
            >
              Invite via Email
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: 'gears', iconStyle: 'regular'}}
              onClick={() => setModalToShow('LMS')}
            >
              Import from LMS
            </Menu.Item>
            <Menu.Item
              leftIconProps={{icon: ['fab', 'google']}}
              onClick={() => setModalToShow('googleClassroom')}
            >
              Import from Google Classroom
            </Menu.Item>
            {canAddCoteacher && (
              <>
                <Menu.Item
                  className='mt-x2 px-x0-half py-x1'
                  padding='none'
                  readonly
                  textProps={{color: 'secondary', size: 's'}}
                >
                  Invite Teachers
                </Menu.Item>
                <Menu.Item
                  leftIconProps={{icon: 'person-chalkboard', iconStyle: 'regular'}}
                  onClick={() => setModalToShow('coteacher')}
                >
                  Add co-teacher
                </Menu.Item>
              </>
            )}
          </Menu.Content>
        </Menu>
        {StudentModal && <StudentModal classroom={classroom} handleClose={close} />}
        {/* Immediate feedback while the roster fetch resolves; closing cancels the pending modal. */}
        {modalToShow && !fullClassroom && (
          <Modal ariaLabel='Loading class information' handleClose={close}>
            {() => <LoadingSpinner className='p-x6' icon='loader' iconStyle='light' size={3} />}
          </Modal>
        )}
      </TypedManageRosterContext.Provider>
      {modalToShow === 'coteacher' && fullClassroom && (
        <TypedPeopleContext.Provider
          value={{classroom, queries: {coteachers: getCoteachersQuery(classroomId)}}}
        >
          <AddCoteacherModal handleClose={close} />
        </TypedPeopleContext.Provider>
      )}
    </>
  );
};
